import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Instagram, Mail, Phone } from "lucide-react";
import { EASE, Kicker, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { BUSINESS, MARQUEE_ITEMS } from "@/data/site";
import { useSiteData } from "@/context/SiteData";
import { sendMessage } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";

export default function Contact() {
  usePageMeta(
    "Contact | Diamond's Divine Arts",
    "Get in touch with Natalia of Diamond's Divine Arts — commissions, consultations, prints, and creative collaborations."
  );

  const { t } = useSiteData();
  const email = t("contact_email", BUSINESS.email);
  const phone = t("contact_phone", BUSINESS.phone);
  const phoneHref = `tel:${String(phone).replace(/[^0-9+]/g, "")}`;
  const instagram = t("contact_instagram", BUSINESS.instagram);
  const instagramUrl = `https://www.instagram.com/${String(instagram).replace("@", "")}`;

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your name.");
    if (!/\S+@\S+\.\S+/.test(form.email)) return toast.error("Please enter a valid email address.");
    if (form.message.trim().length < 5) return toast.error("Please write a short message.");
    setSending(true);
    try {
      await sendMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="divine-rays relative overflow-hidden pb-16 pt-16 md:pb-20 md:pt-24" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>Contact</Kicker>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#1C1815] sm:text-5xl lg:text-6xl">
              Start a <span className="italic text-[#9A7A3F]">conversation</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#4A423A]">
              Commissions, consultations, prints, or collaborations — every message reaches Natalia
              directly.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <section className="py-20 md:py-28" data-testid="contact-form-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="rounded-sm border border-[#D4AF37]/40 bg-[#F4EFE6]/80 p-10 text-center md:p-16"
                data-testid="contact-success"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#FAF7F2]">
                  <Check className="h-7 w-7 text-[#9A7A3F]" />
                </div>
                <h2 className="mt-7 font-serif text-3xl font-light text-[#1C1815]">Message received.</h2>
                <p className="mx-auto mt-4 max-w-md text-sm font-light leading-relaxed text-[#4A423A]">
                  Thank you for reaching out — Natalia will reply to you personally. For anything
                  urgent, call {phone}.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", email: "", phone: "", message: "" });
                  }}
                  className="btn-outline mt-8"
                  data-testid="contact-send-another"
                >
                  Send Another Message
                </button>
              </motion.div>
            ) : (
              <Reveal>
                <form
                  onSubmit={submit}
                  className="rounded-sm border border-[#D4AF37]/30 bg-[#FAF7F2]/80 p-6 sm:p-10"
                  data-testid="contact-form"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="kicker">Name</span>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Your full name"
                        className="field mt-2.5"
                        data-testid="contact-name-input"
                      />
                    </label>
                    <label className="block">
                      <span className="kicker">Email</span>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="you@example.com"
                        className="field mt-2.5"
                        data-testid="contact-email-input"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="kicker">Phone (optional)</span>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="(000) 000-0000"
                        className="field mt-2.5"
                        data-testid="contact-phone-input"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="kicker">Message / Project Details</span>
                      <textarea
                        rows={5}
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                        placeholder="Tell us about your project, your space, or the piece you have in mind…"
                        className="field mt-2.5 resize-none"
                        data-testid="contact-message-input"
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn-primary mt-8 w-full sm:w-auto disabled:opacity-60"
                    data-testid="contact-submit-button"
                  >
                    {sending ? "Sending…" : "Send Message"} <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-sm border border-[#D4AF37]/30 bg-[#171310] p-8 text-[#B7AA99] sm:p-10">
                <h2 className="font-serif text-2xl font-normal text-[#FAF7F2]">Reach Natalia directly</h2>
                <ul className="mt-8 space-y-6 text-sm">
                  <li>
                    <span className="kicker !text-[#B89759]">Email</span>
                    <a
                      href={`mailto:${email}`}
                      className="mt-2 flex items-center gap-3 text-[#FAF7F2] transition-colors hover:text-[#D4AF37]"
                      data-testid="contact-email-link"
                    >
                      <Mail className="h-4 w-4 text-[#B89759]" /> {email}
                    </a>
                  </li>
                  <li>
                    <span className="kicker !text-[#B89759]">Phone · tap to call</span>
                    <a
                      href={phoneHref}
                      className="mt-2 flex items-center gap-3 text-[#FAF7F2] transition-colors hover:text-[#D4AF37]"
                      data-testid="contact-phone-link"
                    >
                      <Phone className="h-4 w-4 text-[#B89759]" /> {phone}
                    </a>
                  </li>
                  <li>
                    <span className="kicker !text-[#B89759]">Instagram</span>
                    <a
                      href={instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-3 text-[#FAF7F2] transition-colors hover:text-[#D4AF37]"
                      data-testid="contact-instagram-link"
                    >
                      <Instagram className="h-4 w-4 text-[#B89759]" /> {instagram}
                    </a>
                  </li>
                </ul>
                <div className="mt-10 border-t border-[#FAF7F2]/10 pt-8">
                  <p className="text-sm font-light leading-relaxed">
                    Consultations are held virtually or by appointment. Studio visits can be
                    arranged when booking.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
